import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@/lib/utils";

export type OdsButtonVariant = "highlight" | "primary" | "secondary" | "contrast";
export type OdsButtonSize = "small" | "medium" | "large";

const buttonVariantClass: Record<OdsButtonVariant, string> = {
  highlight: "border-0 bg-[var(--bg-primary)] text-[var(--fg-base)] hover:opacity-90",
  primary: "border-0 bg-[var(--bg-primary)] text-[var(--fg-base)] hover:opacity-90",
  secondary: "border-0 bg-[var(--bg-secondary)] text-[var(--fg-base)] hover:opacity-90",
  contrast: "border-0 bg-[var(--bg-inverse)] text-[var(--fg-inverse)] hover:opacity-90",
};

const buttonSizeClass: Record<OdsButtonSize, string> = {
  small: "h-7 px-3 text-[12px]",
  medium: "h-8 px-5 text-[14px]",
  large: "h-10 px-5 text-[16px]",
};

interface OdsButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  children: ReactNode;
  size?: OdsButtonSize;
  variant?: OdsButtonVariant;
}

export function OdsButton({
  asChild = false,
  children,
  className,
  size = "medium",
  type = "button",
  variant = "primary",
  ...props
}: OdsButtonProps) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      className={cn(
        "inline-flex items-center justify-center rounded-[24px] font-medium leading-[120%] transition-[background-color,border-color,opacity] disabled:pointer-events-none disabled:opacity-50",
        buttonSizeClass[size],
        buttonVariantClass[variant],
        className
      )}
      type={asChild ? undefined : type}
      {...props}
    >
      {children}
    </Comp>
  );
}
